import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiEdit, FiSave, FiX, FiAlertCircle, FiSearch, FiBookOpen } from 'react-icons/fi';
import api from '../../services/api';

const CoursePricingContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 2.5rem;
  font-weight: 500;
  margin: 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const SearchBar = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  
  @media (max-width: 768px) {
    max-width: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: 0;
  font-size: 0.875rem;
  background-color: var(--background-primary);
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
`;

const Filters = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: ${props => props.active ? 'var(--primary-color)' : 'var(--background-primary)'};
  color: ${props => props.active ? 'white' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-light)' : 'var(--background-secondary)'};
  }
`;

const CoursesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const CourseCard = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  transition: var(--transition);
  
  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-primary) 100%);
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const CourseTitle = styled.h3`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const CourseCode = styled.div`
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const Instructor = styled.div`
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
`;

const CardContent = styled.div`
  padding: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const CurrentPricing = styled.div`
  background: var(--background-secondary);
  border-radius: 0;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const PriceDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const PriceLabel = styled.span`
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
`;

const PriceValue = styled.span`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
`;

const StatusBadge = styled.div`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => props.isPaid ? 'var(--success-color)' : 'var(--text-muted)'};
  color: var(--text-inverse);
`;

const PricingForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  background-color: var(--background-primary);
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(26, 54, 93, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  background: var(--background-primary);
  cursor: pointer;
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(26, 54, 93, 0.1);
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const SaveButton = styled.button`
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  padding: 0.75rem 1.5rem;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;

  &:hover:not(:disabled) {
    background: var(--primary-dark);
    transform: translateY(-1px);
  }

  &:disabled {
    background: var(--text-muted);
    cursor: not-allowed;
    transform: none;
  }
`;

const CancelButton = styled.button`
  background: var(--text-muted);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  padding: 0.75rem 1.5rem;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: var(--text-muted);
    transform: translateY(-1px);
  }
`;

const EditButton = styled.button`
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  padding: 0.75rem 1.5rem;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: fit-content;

  &:hover {
    background: var(--primary-dark);
    transform: translateY(-1px);
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 0;
  padding: 1rem;
  color: var(--error-color);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.8rem;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 2rem 1rem;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid var(--background-primary);
  border-radius: 0;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 2rem 1rem;
  }
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--background-secondary);
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: var(--text-secondary);
  font-size: 2rem;
  
  @media (max-width: 768px) {
    width: 64px;
    height: 64px;
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    width: 56px;
    height: 56px;
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }
`;

function CoursePricing() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  // Fetch courses
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching courses from catalog...');
        const response = await api.getPublicCourses();
        console.log('Catalog response:', response);
        
        // Check if response has the expected structure
        if (!response || !response.success) {
          throw new Error(response?.message || response?.error || 'Invalid response from server');
        }
        
        const coursesData = response.courses || [];
        console.log(`Loaded ${coursesData.length} courses from catalog`);

        // Pricing information is already included in the response from /courses/catalog
        const coursesWithPricing = coursesData.map((course) => ({
          ...course,
          price: course.price ?? 0,
          currency: course.currency || 'USD',
          isPaidCourse: course.isPaidCourse ?? false,
          editing: false
        }));

        setCourses(coursesWithPricing);
      } catch (error) {
        console.error('Error fetching courses:', error);
        // Extract error message from API response if available
        let errorMessage = 'Please try again later';
        if (error.message) {
          errorMessage = error.message;
        } else if (error.response && error.response.message) {
          errorMessage = error.response.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
        setError(`Failed to load courses: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const handleEdit = (course) => {
    setEditingCourse({
      ...course,
      price: course.price || 0,
      currency: course.currency || 'USD',
      isPaidCourse: Boolean(course.isPaidCourse)
    });
    setError(null); // Clear any previous errors
  };

  const handleCancel = () => {
    setEditingCourse(null);
  };

  const handleSave = async () => {
    if (!editingCourse) return;

    // Validate: if isPaidCourse is true, price must be greater than 0
    if (editingCourse.isPaidCourse && (!editingCourse.price || parseFloat(editingCourse.price) <= 0)) {
      setError('Please enter a valid price greater than 0 for paid courses.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      // Prepare pricing data - ensure isPaidCourse is a boolean
      const priceValue = editingCourse.isPaidCourse ? (parseFloat(editingCourse.price) || 0) : 0;
      const pricingData = {
        price: priceValue,
        currency: editingCourse.currency || 'USD',
        isPaidCourse: Boolean(editingCourse.isPaidCourse)
      };
      
      console.log('Saving pricing data:', {
        courseId: editingCourse.id,
        pricingData,
        editingCourse
      });
      
      // Make API call to save the pricing
      const response = await api.updateCoursePricing(editingCourse.id, pricingData);
      console.log('Save response:', response);
      
      // Update local state with the saved pricing data (no need to refetch since we just saved it)
      const updatedCourses = courses.map(course => 
        course.id === editingCourse.id 
          ? { 
              ...course, 
              price: priceValue,
              currency: pricingData.currency,
              isPaidCourse: pricingData.isPaidCourse
            }
          : course
      );
      setCourses(updatedCourses);
      
      setEditingCourse(null);
      console.log('Pricing saved successfully for course:', editingCourse.id);
      
    } catch (error) {
      console.error('Error saving pricing:', error);
      setError(`Failed to save pricing: ${error.message || error}`);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditingCourse(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = 
      course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.instructor.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      activeFilter === 'all' || 
      (activeFilter === 'paid' && course.isPaidCourse) ||
      (activeFilter === 'free' && !course.isPaidCourse);
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <CoursePricingContainer>
        <Header>
          <Title>Course Pricing Management</Title>
        </Header>
        <LoadingState>
          Loading course pricing...
        </LoadingState>
      </CoursePricingContainer>
    );
  }

  return (
    <CoursePricingContainer>
      <Header>
        <Title>Course Pricing Management</Title>
        <SearchBar>
          <SearchIcon>
            <FiSearch />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBar>
      </Header>

      <Filters>
        <FilterButton 
          active={activeFilter === 'all'}
          onClick={() => setActiveFilter('all')}
        >
          All Courses
        </FilterButton>
        <FilterButton 
          active={activeFilter === 'paid'}
          onClick={() => setActiveFilter('paid')}
        >
          Paid Courses
        </FilterButton>
        <FilterButton 
          active={activeFilter === 'free'}
          onClick={() => setActiveFilter('free')}
        >
          Free Courses
        </FilterButton>
      </Filters>

      {error && (
        <ErrorMessage>
          <FiAlertCircle size={16} />
          {error}
        </ErrorMessage>
      )}

      {filteredCourses.length === 0 && (
        <EmptyState>
          <EmptyIcon>
            <FiBookOpen />
          </EmptyIcon>
          <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-primary)', fontFamily: "'Inter', sans-serif", fontSize: '1.25rem', fontWeight: '600' }}>
            {searchTerm || activeFilter !== 'all' ? 'No matching courses found' : 'No Courses Available'}
          </h3>
          <p style={{ margin: 0, fontSize: '1rem' }}>
            {searchTerm || activeFilter !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'There are no courses available for pricing management.'
            }
          </p>
        </EmptyState>
      )}

      {filteredCourses.length > 0 && (
        <CoursesList>
          {filteredCourses.map((course, index) => (
            <CourseCard
              key={course.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0 }}
            >
              <CardHeader>
                <CourseTitle>{course.name}</CourseTitle>
                <CourseCode>{course.code}</CourseCode>
                <Instructor>Instructor: {course.instructor}</Instructor>
              </CardHeader>

              <CardContent>
                {editingCourse?.id === course.id ? (
                  <PricingForm onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                    <FormGroup>
                      <CheckboxContainer>
                        <Checkbox
                          type="checkbox"
                          id={`paid-${course.id}`}
                          checked={Boolean(editingCourse.isPaidCourse)}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            handleInputChange('isPaidCourse', isChecked);
                            // If unchecking paid course, reset price to 0
                            if (!isChecked) {
                              handleInputChange('price', 0);
                            }
                          }}
                        />
                        <CheckboxLabel htmlFor={`paid-${course.id}`}>
                          This is a paid course
                        </CheckboxLabel>
                      </CheckboxContainer>
                    </FormGroup>

                    {editingCourse.isPaidCourse && (
                      <>
                        <FormGroup>
                          <Label htmlFor={`price-${course.id}`}>Price</Label>
                          <Input
                            type="number"
                            id={`price-${course.id}`}
                            value={editingCourse.price || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleInputChange('price', value === '' ? '' : parseFloat(value) || 0);
                            }}
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                          />
                        </FormGroup>

                        <FormGroup>
                          <Label htmlFor={`currency-${course.id}`}>Currency</Label>
                          <Select
                            id={`currency-${course.id}`}
                            value={editingCourse.currency}
                            onChange={(e) => handleInputChange('currency', e.target.value)}
                          >
                            <option value="USD">USD - US Dollar</option>
                            <option value="EUR">EUR - Euro</option>
                            <option value="GBP">GBP - British Pound</option>
                            <option value="CAD">CAD - Canadian Dollar</option>
                          </Select>
                        </FormGroup>
                      </>
                    )}

                    <ButtonGroup>
                      <SaveButton type="submit" disabled={saving}>
                        {saving ? <LoadingSpinner /> : <FiSave size={16} />}
                        Save
                      </SaveButton>
                      <CancelButton type="button" onClick={handleCancel}>
                        <FiX size={16} />
                        Cancel
                      </CancelButton>
                    </ButtonGroup>
                  </PricingForm>
                ) : (
                  <>
                    <CurrentPricing>
                      <PriceDisplay>
                        <PriceLabel>Current Price:</PriceLabel>
                        <PriceValue>
                          {course.isPaidCourse ? `$${course.price} ${course.currency}` : 'Free'}
                        </PriceValue>
                      </PriceDisplay>
                      <StatusBadge isPaid={course.isPaidCourse}>
                        {course.isPaidCourse ? 'Paid Course' : 'Free Course'}
                      </StatusBadge>
                    </CurrentPricing>

                    <ButtonGroup>
                      <EditButton onClick={() => handleEdit(course)}>
                        <FiEdit size={16} />
                        Edit Pricing
                      </EditButton>
                    </ButtonGroup>
                  </>
                )}
              </CardContent>
            </CourseCard>
          ))}
        </CoursesList>
      )}
    </CoursePricingContainer>
  );
}

export default CoursePricing;